/**
 * AiBgGenerator — prompt-driven background generator for Studio.
 *
 * Props:
 *   onPick(dataUrl: string)  — fires when the seller selects a background
 *                              (either fresh from generation or from history)
 *   compact?: boolean        — render as a tight inline panel (default true)
 *
 * Saves every successful generation to the seller's library via
 * /api/seller/studio/ai-backgrounds and renders the last 5 as a quick-pick
 * thumbnail strip below the prompt.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Sparkles, Loader2, Trash2 } from "lucide-react";
import { api } from "../lib/api";

const STYLES = [
  { id: "auto",      label: "Auto" },
  { id: "studio",    label: "Studio" },
  { id: "cinematic", label: "Cinematic" },
  { id: "outdoor",   label: "Outdoor" },
  { id: "abstract",  label: "Abstract" },
  { id: "neon",      label: "Neon" },
];

export default function AiBgGenerator({ onPick, compact = true }) {
  const [prompt, setPrompt]     = useState("");
  const [style, setStyle]       = useState("auto");
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState("");
  const [library, setLibrary]   = useState([]);

  const loadLibrary = useCallback(async () => {
    try {
      const { data } = await api.get("/seller/studio/ai-backgrounds?limit=5");
      setLibrary(Array.isArray(data) ? data : []);
    } catch (e) {
      // 401 (logged-out preview) — silently empty
      setLibrary([]);
    }
  }, []);

  useEffect(() => { loadLibrary(); }, [loadLibrary]);

  const generate = async () => {
    if (!prompt.trim() || busy) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post(
        "/seller/studio/ai-backgrounds/generate",
        { prompt: prompt.trim(), style }
      );
      // Optimistic prepend
      setLibrary((arr) => [data, ...arr].slice(0, 5));
      // Auto-select the brand-new background
      onPick?.(data.image_data_url);
    } catch (e) {
      setError(e?.response?.data?.detail || "Generation failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (bg_id) => {
    try {
      await api.delete(`/seller/studio/ai-backgrounds/${bg_id}`);
      setLibrary((arr) => arr.filter((b) => b.bg_id !== bg_id));
    } catch {/* noop */}
  };

  return (
    <div className={`grid gap-2 ${compact ? "" : "mt-3"}`} data-testid="ai-bg-generator">
      <div className="flex items-center gap-2">
        <Sparkles size={14} className="text-[#D4FF00]"/>
        <input
          className="input-dark text-xs flex-1"
          placeholder="Describe a background… (e.g. golden-hour rooftop in Tokyo)"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && generate()}
          disabled={busy}
          data-testid="ai-bg-prompt-input"/>
        <button
          type="button"
          onClick={generate}
          disabled={busy || !prompt.trim()}
          className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1"
          data-testid="ai-bg-generate-btn">
          {busy
            ? <><Loader2 size={12} className="animate-spin"/> Generating…</>
            : <><Sparkles size={12}/> Generate</>}
        </button>
      </div>

      <div className="flex flex-wrap gap-1" data-testid="ai-bg-style-row">
        <span className="text-[10px] text-[#A1A1AA] uppercase font-bold mr-1 self-center">Style</span>
        {STYLES.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setStyle(s.id)}
            data-testid={`ai-bg-style-${s.id}`}
            className={`chip text-[10px] ${style === s.id ? "active" : ""}`}>
            {s.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="text-[10px] text-[#FF007F]" data-testid="ai-bg-error">{error}</div>
      )}

      {library.length > 0 && (
        <div className="pt-2 border-t border-[#3D2845]">
          <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-1">
            Recent · {library.length}/5
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="ai-bg-library">
            {library.map((b) => (
              <div key={b.bg_id} className="relative shrink-0" data-testid={`ai-bg-item-${b.bg_id}`}>
                <button
                  type="button"
                  onClick={() => onPick?.(b.image_data_url)}
                  className="block w-16 h-24 rounded-lg border-2 border-transparent hover:border-[#D4FF00] transition overflow-hidden"
                  style={{
                    background: `url(${b.image_data_url}) center/cover #1A0F1F`,
                  }}
                  title={b.prompt}
                  data-testid={`ai-bg-pick-${b.bg_id}`}/>
                <button
                  type="button"
                  onClick={() => remove(b.bg_id)}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#0F0817] border border-[#3D2845] text-[#FF007F] flex items-center justify-center"
                  aria-label="Delete background"
                  data-testid={`ai-bg-delete-${b.bg_id}`}>
                  <Trash2 size={10}/>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
